import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { activationsApiRequest } from '@/lib/activationsApiHelpers';
import { useToast } from '@/hooks/use-toast';
import type {
  ActivationSubmissionResponse,
  ActivationGenerationAdminResponse,
  ActivationResultResponse,
  ActivationLeadResponse,
  RejectGenerationRequest,
  RegenerateGenerationRequest,
} from '@/types/generated/activations.generated';

const activationScope = (activationId: string) => ['activations', activationId] as const;
const eventScope = (eventId: string) => ['activation-events', eventId] as const;

/** Submissions for a single activation. */
export function useActivationSubmissions(activationId: string | undefined) {
  return useQuery<ActivationSubmissionResponse[]>({
    queryKey: [...activationScope(activationId ?? ''), 'submissions'],
    queryFn: () => activationsApiRequest('GET', `/admin/activations/${activationId}/submissions`),
    enabled: !!activationId,
  });
}

/** Media generations for a single activation (full operational detail). */
export function useActivationGenerations(activationId: string | undefined) {
  return useQuery<ActivationGenerationAdminResponse[]>({
    queryKey: [...activationScope(activationId ?? ''), 'generations'],
    queryFn: () => activationsApiRequest('GET', `/admin/activations/${activationId}/generations`),
    enabled: !!activationId,
  });
}

/** Unified results (with delivery status) for a single activation. */
export function useActivationResults(activationId: string | undefined) {
  return useQuery<ActivationResultResponse[]>({
    queryKey: [...activationScope(activationId ?? ''), 'results'],
    queryFn: () => activationsApiRequest('GET', `/admin/activations/${activationId}/results`),
    enabled: !!activationId,
  });
}

/** All submissions across an event (results overview). */
export function useEventSubmissions(eventId: string | undefined) {
  return useQuery<ActivationSubmissionResponse[]>({
    queryKey: [...eventScope(eventId ?? ''), 'submissions'],
    queryFn: () => activationsApiRequest('GET', `/admin/activations/events/${eventId}/submissions`),
    enabled: !!eventId,
  });
}

/** Captured leads (identified participants) for an event. */
export function useEventLeads(eventId: string | undefined) {
  return useQuery<ActivationLeadResponse[]>({
    queryKey: [...eventScope(eventId ?? ''), 'leads'],
    queryFn: () => activationsApiRequest('GET', `/admin/activations/events/${eventId}/leads`),
    enabled: !!eventId,
  });
}

/**
 * Approval-queue mutations for OperatorCurated photo booths. Each operates on a single
 * generation within an activation and invalidates that activation's generations cache so
 * the review queue reflects the new approval state. `activationId` scopes the cache key.
 */
export function useApproveGeneration(activationId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (generationId: string) =>
      activationsApiRequest<ActivationGenerationAdminResponse>(
        'POST',
        `/admin/activations/${activationId}/generations/${generationId}/approve`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...activationScope(activationId), 'generations'],
      });
      toast({ title: 'Generation approved', description: 'The asset is approved for delivery.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export function useRejectGeneration(activationId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({
      generationId,
      data,
    }: {
      generationId: string;
      data: RejectGenerationRequest;
    }) =>
      activationsApiRequest<ActivationGenerationAdminResponse>(
        'POST',
        `/admin/activations/${activationId}/generations/${generationId}/reject`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...activationScope(activationId), 'generations'],
      });
      toast({ title: 'Generation rejected', description: 'The asset will not be delivered.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export function useRegenerateGeneration(activationId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({
      generationId,
      data,
    }: {
      generationId: string;
      data: RegenerateGenerationRequest;
    }) =>
      activationsApiRequest<ActivationGenerationAdminResponse>(
        'POST',
        `/admin/activations/${activationId}/generations/${generationId}/regenerate`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...activationScope(activationId), 'generations'],
      });
      toast({ title: 'Regenerating', description: 'A replacement asset is being generated.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to regenerate. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Executes the raffle draw / leaderboard for an activation. Persists results, publishes
 * delivery, and broadcasts to the live session. Returns the created results.
 */
export function useDrawRaffle() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (activationId: string) =>
      activationsApiRequest<ActivationResultResponse[]>(
        'POST',
        `/admin/activations/${activationId}/raffle/draw`,
      ),
    onSuccess: (_data, activationId) => {
      queryClient.invalidateQueries({ queryKey: [...activationScope(activationId), 'results'] });
      toast({ title: 'Raffle drawn', description: 'Results have been generated and delivery queued.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to draw raffle. Please try again.',
        variant: 'destructive',
      });
    },
  });
}
